import { api, ApiError } from "../api.js";
import { esc, displayName, timeAgo, toast } from "../utils.js";
import { isSiteAdmin } from "../auth.js";
import { navigate } from "../app.js";
import { confirmAction } from "../confirmDialog.js";
import { attachmentsHtml } from "../chatPanel.js";
import { openPhotoViewer } from "../photoViewer.js";

const STATUSES = [
  ["open", "Open"],
  ["resolved", "Resolved"],
  ["dismissed", "Dismissed"],
];

const REASON_LABELS = {
  spam: "Spam",
  harassment: "Harassment",
  inappropriate: "Inappropriate",
  misinformation: "Misinformation",
  other: "Other",
};

/** The moderation queue. Site admins only — the API enforces it too, this
 * just avoids showing a page that would only ever return 403. */
export async function render(container, params, query) {
  if (!isSiteAdmin()) {
    container.innerHTML = `
      <div class="dc-empty text-center py-5">
        <i class="bi bi-shield-lock" style="font-size:2.5rem;"></i>
        <h1 class="h4 mt-3">Moderators only</h1>
        <p class="text-slate">This page is for site administrators.</p>
      </div>`;
    return;
  }

  let status = query?.get("status") || "open";
  if (!STATUSES.some(([value]) => value === status)) status = "open";

  container.innerHTML = `
    <h1 class="display-heading h3 mb-3">Moderation</h1>
    <ul class="nav nav-tabs mb-3" id="mod-tabs">
      ${STATUSES.map(
        ([value, label]) => `
        <li class="nav-item">
          <a class="nav-link ${value === status ? "active" : ""}" href="#/moderation?status=${value}">${label}</a>
        </li>`
      ).join("")}
    </ul>
    <div id="mod-list"><div class="text-center py-5"><div class="spinner-border text-primary"></div></div></div>`;

  const list = container.querySelector("#mod-list");

  async function load() {
    try {
      const reports = await api.get("/reports", { status });
      draw(reports);
    } catch (err) {
      list.innerHTML = `<div class="dc-empty"><i class="bi bi-exclamation-triangle"></i><div>${
        esc(err instanceof ApiError ? err.message : "Could not load the queue.")
      }</div></div>`;
    }
  }

  function cardHtml(report) {
    const reason = REASON_LABELS[report.reason] || report.reason;
    const kind = report.target_kind === "chat_message" ? "chat message" : report.target_kind;
    // A cohort's chat is for its members, so there's no page to go to: the
    // report's copy is what a moderator sees, and the message is deleted here.
    const chatMessage = report.target_kind === "chat_message";
    // A report outlives the content it flagged, so say plainly whether
    // there is still anything to look at.
    const gone = !report.target_exists;
    // parent_post_id resolves to the post itself for a post report, and
    // to the comment's parent for a comment report. A comment report also
    // carries ?comment=, so the moderator lands on the actual comment
    // instead of a long thread they have to search.
    const link = report.parent_post_id
      ? `#/post/${report.parent_post_id}${report.comment_id ? `?comment=${report.comment_id}` : ""}`
      : null;
    return `
      <div class="card mb-3" data-report-id="${report.id}">
        <div class="card-body">
          <div class="d-flex flex-wrap align-items-center gap-2 mb-2">
            <span class="badge text-bg-danger">${esc(reason)}</span>
            <span class="badge text-bg-light border">${esc(kind)}</span>${chatMessage && report.community
              ? `<span class="text-slate small">in ${esc(report.community.name)}'s chat</span>` : ""}
            ${gone ? `<span class="badge text-bg-secondary">content deleted</span>` : ""}
            <span class="text-slate small ms-auto">${timeAgo(report.created_at)}</span>
          </div>
          ${report.details ? `<p class="mb-2 dc-user-text">${esc(report.details)}</p>` : ""}
          <blockquote class="border-start border-3 ps-3 text-slate small mb-2 dc-user-text">${
            esc(report.content_snapshot || "(no text)")
          }</blockquote>
          ${report.attachments?.length
            ? `<div class="mb-2 dc-report-attachments">${attachmentsHtml(report.attachments)}</div>` : ""}
          <div class="text-slate small mb-2">
            Reported by ${esc(displayName(report.reporter))}
            ${report.reviewed_at
              ? ` · reviewed ${timeAgo(report.reviewed_at)} by ${esc(displayName(report.reviewed_by))}`
              : ""}
          </div>
          ${report.resolution_note ? `<p class="small mb-2"><em>${esc(report.resolution_note)}</em></p>` : ""}
          <div class="d-flex flex-wrap gap-2">
            ${link && !gone
              ? `<a class="btn btn-sm btn-primary" href="${link}">
                   <i class="bi bi-box-arrow-up-right me-1"></i>Go to ${esc(kind)}
                 </a>`
              : ""}
            ${chatMessage && !gone && report.chat_id
              ? `<button class="btn btn-sm btn-outline-danger" data-delete-message>
                   <i class="bi bi-trash me-1"></i>Delete message</button>`
              : ""}
            ${report.status === "open"
              ? `<button class="btn btn-sm btn-success" data-act="resolved">Mark resolved</button>
                 <button class="btn btn-sm btn-outline-secondary" data-act="dismissed">Dismiss</button>`
              : `<button class="btn btn-sm btn-outline-secondary" data-act="open">Reopen</button>`}
          </div>
        </div>
      </div>`;
  }

  function draw(reports) {
    if (!reports.length) {
      list.innerHTML = `<div class="dc-empty"><i class="bi bi-check2-circle"></i><div>Nothing ${esc(status)}.</div></div>`;
      return;
    }
    list.innerHTML = reports.map(cardHtml).join("");
    list.querySelectorAll("[data-act]").forEach((btn) => {
      btn.addEventListener("click", () => act(btn));
    });
    list.querySelectorAll("[data-delete-message]").forEach((btn) => {
      btn.addEventListener("click", () => deleteMessage(btn, reports));
    });
    // A reported chat message's photos enlarge here, as in the chat.
    list.querySelectorAll("[data-photo-url]").forEach((tile) => {
      tile.addEventListener("click", () => openPhotoViewer(tile.dataset.photoUrl));
    });
  }

  /** A reported chat message, from here: its members then see "Deleted by an admin". */
  async function deleteMessage(btn, reports) {
    const id = Number(btn.closest("[data-report-id]").dataset.reportId);
    const report = reports.find((r) => r.id === id);
    if (!report) return;
    const ok = await confirmAction({
      title: "Delete this chat message?",
      body: `Its cohort's members will see “Deleted by an admin” in its place. The report keeps what it said.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    btn.disabled = true;
    try {
      await api.delete(`/chats/${report.chat_id}/messages/${report.chat_message_id}`);
      toast("Message deleted.", "success");
      load();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not delete that message.", "danger");
      btn.disabled = false;
    }
  }

  async function act(btn) {
    const card = btn.closest("[data-report-id]");
    const id = Number(card.dataset.reportId);
    const next = btn.dataset.act;

    let note = null;
    if (next === "resolved" || next === "dismissed") {
      const ok = await confirmAction({
        title: next === "resolved" ? "Mark resolved?" : "Dismiss this report?",
        body: next === "resolved"
          ? "Use this once you've acted on it — removed the content, warned the author, whatever was needed."
          : "Use this when there's nothing wrong with the content.",
        confirmLabel: next === "resolved" ? "Mark resolved" : "Dismiss",
        variant: next === "resolved" ? "success" : "secondary",
      });
      if (!ok) return;
      note = next === "resolved" ? "Resolved from the moderation queue." : "Dismissed as no action needed.";
    }

    btn.disabled = true;
    try {
      await api.patch(`/reports/${id}`, { status: next, ...(note ? { resolution_note: note } : {}) });
      toast(`Report ${next}.`, "success");
      load();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : "Could not update that report.", "danger");
      btn.disabled = false;
    }
  }

  load();
}
